// ImportItemEditor — generic form renderer for import items.
// Renders appropriate inputs based on field schemas from fieldSchemas.ts.
// Edits modify a local working copy; the parent is notified via onChange.

import { useState, useCallback, useMemo } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AlertTriangle, X, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ImportValidationError } from '@/lib/api/imports'
import type { TreeNode } from './importTree'
import {
  getFieldSchemas,
  collectTokens,
  type FieldSchema,
} from './fieldSchemas'

// --- Props ---

interface ImportItemEditorProps {
  /** The tree node being edited */
  node: TreeNode
  /** The full import payload (for populating token_ref dropdowns) */
  payload: Record<string, unknown>
  /** Called when a field value changes. Passes the field key and new value. */
  onChange: (key: string, value: unknown) => void
}

// --- Component ---

export function ImportItemEditor({ node, payload, onChange }: ImportItemEditorProps) {
  if (node.type === 'section') {
    return <SectionDetail node={node} />
  }

  const schemas = getFieldSchemas(node.itemType ?? '', node.section)
  if (schemas.length === 0) {
    return (
      <div className="space-y-4">
        <ItemHeader node={node} />
        <p className="text-sm text-muted-foreground">
          No field schema defined for item type "{node.itemType}".
        </p>
        {node.itemData && (
          <pre className="max-h-96 overflow-auto rounded bg-muted p-3 text-xs">
            {JSON.stringify(node.itemData, null, 2)}
          </pre>
        )}
      </div>
    )
  }

  const data = node.itemData ?? {}

  return (
    <div className="space-y-4">
      <ItemHeader node={node} />

      {/* Validation errors */}
      {node.errors.length > 0 && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3">
          <p className="mb-1 text-sm font-medium text-destructive">
            {node.errors.length} validation error{node.errors.length !== 1 ? 's' : ''}
          </p>
          {node.errors.map((err, i) => (
            <p key={i} className="text-sm text-destructive">{err.message}</p>
          ))}
        </div>
      )}

      {/* Fields */}
      <div className="space-y-3">
        {schemas.map((schema) => (
          <FieldRenderer
            key={schema.key}
            schema={schema}
            value={data[schema.key]}
            payload={payload}
            errors={node.errors}
            onChange={(val) => onChange(schema.key, val)}
          />
        ))}
      </div>
    </div>
  )
}

// --- Section detail (unchanged from original) ---

function SectionDetail({ node }: { node: TreeNode }) {
  const itemCount = node.children.filter((c) => c.type === 'item').length
  const subSectionCount = node.children.filter((c) => c.type === 'section').length
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{node.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subSectionCount > 0
            ? `${subSectionCount} sub-section${subSectionCount !== 1 ? 's' : ''}`
            : `${itemCount} item${itemCount !== 1 ? 's' : ''}`}
        </p>
      </div>
      {node.errors.length > 0 && (
        <div className="space-y-1">
          <p className="text-sm font-medium text-destructive">Section-level errors:</p>
          {node.errors.map((err, i) => (
            <p key={i} className="text-sm text-destructive">{err.message}</p>
          ))}
        </div>
      )}
    </div>
  )
}

// --- Item header ---

function ItemHeader({ node }: { node: TreeNode }) {
  return (
    <div>
      <h2 className="text-lg font-semibold">{node.label}</h2>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>Token: {node.token}</span>
        <span>·</span>
        <span>Section: {node.section}</span>
        {node.itemType && (
          <>
            <span>·</span>
            <span>Type: {node.itemType}</span>
          </>
        )}
      </div>
    </div>
  )
}

// --- Field renderer ---

interface FieldRendererProps {
  schema: FieldSchema
  value: unknown
  payload: Record<string, unknown>
  errors: ImportValidationError[]
  onChange: (value: unknown) => void
}

function FieldRenderer({ schema, value, payload, errors, onChange }: FieldRendererProps) {
  // Check if this field is mentioned in any validation error
  const fieldHasError = errors.some((e) =>
    e.message.includes(`"${schema.key}"`) || e.message.includes(`'${schema.key}'`),
  )

  switch (schema.type) {
    case 'text':
    case 'date':
      return (
        <FieldWrapper schema={schema} hasError={fieldHasError}>
          <Input
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={schema.placeholder}
            readOnly={schema.readOnly}
            className={cn(schema.readOnly && 'bg-muted')}
          />
        </FieldWrapper>
      )

    case 'textarea':
      return (
        <FieldWrapper schema={schema} hasError={fieldHasError}>
          <Textarea
            value={typeof value === 'string' ? value : ''}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
            placeholder={schema.placeholder}
            rows={3}
          />
        </FieldWrapper>
      )

    case 'number':
      return (
        <FieldWrapper schema={schema} hasError={fieldHasError}>
          <Input
            type="number"
            value={typeof value === 'number' ? value : ''}
            onChange={(e) => {
              const num = e.target.value === '' ? 0 : parseInt(e.target.value, 10)
              onChange(isNaN(num) ? 0 : num)
            }}
            placeholder={schema.placeholder}
          />
        </FieldWrapper>
      )

    case 'select':
      return (
        <FieldWrapper schema={schema} hasError={fieldHasError}>
          <Select
            value={typeof value === 'string' ? value : ''}
            onValueChange={onChange}
          >
            <SelectTrigger>
              <SelectValue placeholder={schema.placeholder ?? 'Select...'} />
            </SelectTrigger>
            <SelectContent>
              {schema.options?.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldWrapper>
      )

    case 'token_ref':
      return (
        <TokenRefField
          schema={schema}
          value={value}
          payload={payload}
          hasError={fieldHasError}
          onChange={onChange}
          lookupTypeFilter={schema.lookupTypeFilter}
        />
      )

    case 'token_ref_array':
      return (
        <TokenRefArrayField
          schema={schema}
          value={value}
          payload={payload}
          hasError={fieldHasError}
          onChange={onChange}
          lookupTypeFilter={schema.lookupTypeFilter}
        />
      )

    case 'string_array':
      return (
        <StringArrayField
          schema={schema}
          value={value}
          hasError={fieldHasError}
          onChange={onChange}
        />
      )

    case 'scoring':
      return (
        <ScoringField
          schema={schema}
          value={value}
          payload={payload}
          hasError={fieldHasError}
          onChange={onChange}
        />
      )

    default:
      return (
        <FieldWrapper schema={schema} hasError={fieldHasError}>
          <Input
            value={typeof value === 'string' ? String(value) : JSON.stringify(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
            placeholder={schema.placeholder}
          />
        </FieldWrapper>
      )
  }
}

// --- Field wrapper with label ---

function FieldWrapper({
  schema,
  hasError,
  children,
}: {
  schema: FieldSchema
  hasError: boolean
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label className={cn('flex items-center gap-1.5', hasError && 'text-destructive')}>
        {schema.label}
        {schema.required && <span className="text-destructive">*</span>}
        {hasError && <AlertTriangle className="h-3 w-3" />}
      </Label>
      {children}
    </div>
  )
}

// --- Token reference (single) ---

function TokenRefField({
  schema,
  value,
  payload,
  hasError,
  onChange,
  lookupTypeFilter,
}: {
  schema: FieldSchema
  value: unknown
  payload: Record<string, unknown>
  hasError: boolean
  onChange: (value: unknown) => void
  lookupTypeFilter?: string[]
}) {
  const options = useMemo(
    () => collectTokens(payload, schema.tokenRefSections ?? [], lookupTypeFilter),
    [payload, schema.tokenRefSections, lookupTypeFilter],
  )

  const strValue = typeof value === 'string' ? value : ''

  return (
    <FieldWrapper schema={schema} hasError={hasError}>
      <Select value={strValue} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder="Select token..." />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.token} value={opt.token}>
              <span>{opt.label}</span>
              {opt.label !== opt.token && (
                <span className="ml-1.5 text-xs text-muted-foreground">({opt.token})</span>
              )}
            </SelectItem>
          ))}
          {options.length === 0 && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              No tokens available
            </div>
          )}
        </SelectContent>
      </Select>
      {/* Show current value if not in options (e.g. from a different source) */}
      {strValue && !options.some((o) => o.token === strValue) && (
        <p className="text-xs text-amber-600">
          Current value "{strValue}" not found in available tokens
        </p>
      )}
    </FieldWrapper>
  )
}

// --- Token reference array (multi) ---

function TokenRefArrayField({
  schema,
  value,
  payload,
  hasError,
  onChange,
  lookupTypeFilter,
}: {
  schema: FieldSchema
  value: unknown
  payload: Record<string, unknown>
  hasError: boolean
  onChange: (value: unknown) => void
  lookupTypeFilter?: string[]
}) {
  const options = useMemo(
    () => collectTokens(payload, schema.tokenRefSections ?? [], lookupTypeFilter),
    [payload, schema.tokenRefSections, lookupTypeFilter],
  )

  const currentValues: string[] = Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string')
    : []

  const availableOptions = options.filter((o) => !currentValues.includes(o.token))

  const handleAdd = useCallback(
    (token: string) => {
      onChange([...currentValues, token])
    },
    [currentValues, onChange],
  )

  const handleRemove = useCallback(
    (token: string) => {
      onChange(currentValues.filter((v) => v !== token))
    },
    [currentValues, onChange],
  )

  return (
    <FieldWrapper schema={schema} hasError={hasError}>
      {/* Current values as badges */}
      {currentValues.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {currentValues.map((token) => {
            const opt = options.find((o) => o.token === token)
            return (
              <Badge key={token} variant="secondary" className="gap-1 pr-1">
                <span>{opt?.label ?? token}</span>
                <button
                  type="button"
                  onClick={() => handleRemove(token)}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )
          })}
        </div>
      )}

      {/* Add dropdown */}
      {availableOptions.length > 0 && (
        <Select value="" onValueChange={handleAdd}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Add token..." />
          </SelectTrigger>
          <SelectContent>
            {availableOptions.map((opt) => (
              <SelectItem key={opt.token} value={opt.token}>
                <span>{opt.label}</span>
                {opt.label !== opt.token && (
                  <span className="ml-1.5 text-xs text-muted-foreground">({opt.token})</span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {options.length === 0 && currentValues.length === 0 && (
        <p className="text-xs text-muted-foreground">No tokens available</p>
      )}
    </FieldWrapper>
  )
}

// --- String array (e.g. lookup path) ---

function StringArrayField({
  schema,
  value,
  hasError,
  onChange,
}: {
  schema: FieldSchema
  value: unknown
  hasError: boolean
  onChange: (value: unknown) => void
}) {
  const items: string[] = Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string')
    : []

  const [draft, setDraft] = useState('')

  const handleAdd = useCallback(() => {
    const trimmed = draft.trim()
    if (!trimmed) return
    onChange([...items, trimmed])
    setDraft('')
  }, [items, draft, onChange])

  const handleRemove = useCallback(
    (index: number) => {
      onChange(items.filter((_, i) => i !== index))
    },
    [items, onChange],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleAdd()
      }
    },
    [handleAdd],
  )

  return (
    <FieldWrapper schema={schema} hasError={hasError}>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {items.map((item, i) => (
            <Badge key={i} variant="outline" className="gap-1 pr-1">
              <span>{item}</span>
              <button
                type="button"
                onClick={() => handleRemove(i)}
                className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <div className="flex gap-1.5">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={schema.placeholder ?? 'Add item...'}
          className="flex-1"
        />
        <Button type="button" variant="outline" size="icon" onClick={handleAdd} disabled={!draft.trim()}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </FieldWrapper>
  )
}

// --- Risk scoring (nested: likelihood + impacts) ---

interface ScoringValue {
  likelihood?: number
  impacts?: Array<{ table: string; level: number }>
}

function ScoringField({
  schema,
  value,
  payload,
  hasError,
  onChange,
}: {
  schema: FieldSchema
  value: unknown
  payload: Record<string, unknown>
  hasError: boolean
  onChange: (value: unknown) => void
}) {
  const scoring: ScoringValue | null =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as ScoringValue)
      : null

  // Collect impact table tokens from lookups
  const impactTableTokens = useMemo(
    () => collectTokens(payload, ['lookups']).filter((t) => {
      // Filter to only impact_table lookups
      const lookups = payload['lookups']
      if (!Array.isArray(lookups)) return false
      return lookups.some(
        (l: Record<string, unknown>) =>
          l.token === t.token && l.type === 'impact_table',
      )
    }),
    [payload],
  )

  const handleToggle = useCallback(() => {
    if (scoring) {
      onChange(null)
    } else {
      onChange({ likelihood: 0, impacts: [] })
    }
  }, [scoring, onChange])

  const handleLikelihood = useCallback(
    (val: string) => {
      const num = val === '' ? 0 : parseInt(val, 10)
      onChange({ ...(scoring ?? { impacts: [] }), likelihood: isNaN(num) ? 0 : num })
    },
    [scoring, onChange],
  )

  const handleImpactLevel = useCallback(
    (index: number, level: string) => {
      const impacts = [...(scoring?.impacts ?? [])]
      const num = level === '' ? 0 : parseInt(level, 10)
      const existing = impacts[index]
      impacts[index] = { table: existing?.table ?? '', level: isNaN(num) ? 0 : num }
      onChange({ ...(scoring ?? {}), impacts })
    },
    [scoring, onChange],
  )

  const handleAddImpact = useCallback(
    (table: string) => {
      const impacts = [...(scoring?.impacts ?? []), { table, level: 0 }]
      onChange({ ...(scoring ?? { likelihood: 0 }), impacts })
    },
    [scoring, onChange],
  )

  const handleRemoveImpact = useCallback(
    (index: number) => {
      const impacts = (scoring?.impacts ?? []).filter((_, i) => i !== index)
      onChange({ ...(scoring ?? { likelihood: 0 }), impacts })
    },
    [scoring, onChange],
  )

  const usedTables = new Set((scoring?.impacts ?? []).map((i) => i.table))
  const availableTables = impactTableTokens.filter((t) => !usedTables.has(t.token))

  return (
    <FieldWrapper schema={schema} hasError={hasError}>
      <div className="rounded-md border p-3 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">
            {scoring ? 'Configured' : 'Not set'}
          </span>
          <Button type="button" variant="outline" size="sm" onClick={handleToggle}>
            {scoring ? 'Clear' : 'Set Score'}
          </Button>
        </div>

        {scoring && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs">Likelihood Level</Label>
              <Input
                type="number"
                value={scoring.likelihood ?? ''}
                onChange={(e) => handleLikelihood(e.target.value)}
                placeholder="Likelihood level number"
                className="w-32"
              />
            </div>

            {/* Impact scores */}
            <div className="space-y-2">
              <Label className="text-xs">Impact Scores</Label>
              {(scoring.impacts ?? []).map((impact, i) => {
                const tableOpt = impactTableTokens.find((t) => t.token === impact.table)
                return (
                  <div key={i} className="flex items-center gap-2">
                    <Badge variant="outline" className="shrink-0">
                      {tableOpt?.label ?? impact.table}
                    </Badge>
                    <Input
                      type="number"
                      value={impact.level ?? ''}
                      onChange={(e) => handleImpactLevel(i, e.target.value)}
                      placeholder="Level"
                      className="w-24"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemoveImpact(i)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )
              })}

              {availableTables.length > 0 && (
                <Select value="" onValueChange={handleAddImpact}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Add impact table..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTables.map((opt) => (
                      <SelectItem key={opt.token} value={opt.token}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </>
        )}
      </div>
    </FieldWrapper>
  )
}
